import React, { useEffect, useMemo, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, MagnifyingGlass } from "@phosphor-icons/react";

const first = (d) => new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
const last = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);

export default function Payments() {
  const { t } = useI18n();
  const now = new Date();
  const [from, setFrom] = useState(first(new Date(now.getFullYear(), 0, 1)));
  const [to, setTo] = useState(last(new Date(now.getFullYear(), 11, 1)));
  const [statusFilter, setStatusFilter] = useState("all");
  const [schedule, setSchedule] = useState([]);

  const load = () => api.get("/schedule").then((r) => setSchedule(r.data));
  useEffect(() => { load(); }, []);

  const rows = useMemo(
    () => schedule
      .filter((e) => e.due_date >= from && e.due_date <= to)
      .filter((e) => statusFilter === "all" ? true : e.status === statusFilter)
      .sort((a, b) => a.due_date.localeCompare(b.due_date)),
    [schedule, from, to, statusFilter]
  );

  const markPaid = async (e) => {
    try {
      await api.post("/payments", {
        tenant_id: e.tenant_id, year: e.year, month: e.month,
        amount: e.amount, currency: e.currency || "TRY",
        rate_to_try: 1.0, send_receipt: true,
      });
      toast.success(t("email_sent"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const unmark = async (paymentId) => {
    await api.delete(`/payments/${paymentId}`);
    toast.success("OK");
    load();
  };

  // Aggregate by ORIGINAL currency (never mix)
  const expectedByCur = {};
  const collectedByCur = {};
  const overdueByCur = {};
  rows.forEach((r) => {
    const cur = r.currency || "TRY";
    expectedByCur[cur] = (expectedByCur[cur] || 0) + (r.amount || 0);
    if (r.status === "paid") {
      const pc = r.paid_currency || cur;
      collectedByCur[pc] = (collectedByCur[pc] || 0) + (r.paid_amount || 0);
    }
    if (r.status === "overdue") {
      overdueByCur[cur] = (overdueByCur[cur] || 0) + (r.amount || 0);
    }
  });

  const MoneyList = ({ obj, tone = "" }) => {
    const keys = Object.keys(obj);
    if (keys.length === 0) return <div className="font-heading text-3xl font-semibold">—</div>;
    return (
      <div className="space-y-1">
        {keys.map((k) => (
          <div key={k} className={`font-heading text-2xl font-semibold ${tone}`}>{formatMoney(obj[k], k)}</div>
        ))}
      </div>
    );
  };

  const setThisMonth = () => {
    setFrom(first(now));
    setTo(last(now));
  };
  const setThisYear = () => {
    setFrom(first(new Date(now.getFullYear(), 0, 1)));
    setTo(last(new Date(now.getFullYear(), 11, 1)));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{t("payments")}</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900">{t("payments")}</h1>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="pay-filters">
        <div>
          <div className="label-eyebrow mb-1.5">{t("from_date")}</div>
          <Input data-testid="pay-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        </div>
        <div>
          <div className="label-eyebrow mb-1.5">{t("to_date")}</div>
          <Input data-testid="pay-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        </div>
        <div>
          <div className="label-eyebrow mb-1.5">{t("status")}</div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="pay-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_statuses")}</SelectItem>
              <SelectItem value="paid">{t("paid")}</SelectItem>
              <SelectItem value="overdue">{t("overdue_s")}</SelectItem>
              <SelectItem value="pending">{t("pending")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={setThisMonth} data-testid="pay-this-month">{t("this_month")}</Button>
          <Button variant="outline" size="sm" onClick={setThisYear} data-testid="pay-this-year">{t("this_year")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="label-eyebrow">{t("expected_range")}</div>
          <div className="mt-3"><MoneyList obj={expectedByCur} /></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="label-eyebrow">{t("collected")}</div>
          <div className="mt-3"><MoneyList obj={collectedByCur} tone="text-emerald-600" /></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="label-eyebrow">{t("overdue")}</div>
          <div className="mt-3"><MoneyList obj={overdueByCur} tone="text-red-600" /></div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 label-eyebrow">{t("tenant")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("due_date")}</th>
              <th className="text-right px-4 py-3 label-eyebrow">{t("amount")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("status")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500"><MagnifyingGlass size={24} weight="duotone" className="mx-auto mb-2 text-slate-300" />{t("no_data")}</td></tr>}
            {rows.map((e) => (
              <tr key={`${e.tenant_id}-${e.year}-${e.month}`} data-testid={`sched-${e.tenant_id}-${e.year}-${e.month}`}>
                <td className="px-4 py-3 font-medium">{e.tenant_name}</td>
                <td className="px-4 py-3 text-slate-600">{e.due_date}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatMoney(e.amount, e.currency)}</td>
                <td className="px-4 py-3">
                  {e.status === "paid" && <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-emerald-600"><Check size={12} />{t("paid")} <span className="text-slate-400 font-normal ml-1">({formatMoney(e.paid_amount, e.paid_currency)})</span></span>}
                  {e.status === "overdue" && <span className="text-xs uppercase tracking-widest font-bold text-red-600">{t("overdue_s")}</span>}
                  {e.status === "pending" && <span className="text-xs uppercase tracking-widest font-bold text-slate-500">{t("pending")}</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {e.status === "paid" ? (
                    <Button data-testid={`unmark-${e.tenant_id}`} variant="outline" size="sm" onClick={() => unmark(e.payment_id)}>
                      <X size={14} className="mr-1" /> {t("unmark")}
                    </Button>
                  ) : (
                    <Button data-testid={`mark-${e.tenant_id}`} size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => markPaid(e)} title={t("receipt_will_send")}>
                      <Check size={14} className="mr-1" /> {t("mark_paid")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500">💡 {t("receipt_will_send")}</div>
    </div>
  );
}
